/* Impersonation helpers (iter-36 cookie-only).
 *
 * Flow:
 *   1. Owner clicks "Login as <user>" in OrgDeepView.
 *   2. Caller invokes startImpersonation() which POSTs to
 *      /api/owner/users/:id/impersonate. The backend SWAPS the HttpOnly
 *      access/refresh cookies to the target user and stashes the owner's
 *      previous refresh in `owner_resume_token` cookie.
 *   3. We mark a session flag `hbf_impersonating` in sessionStorage
 *      (non-sensitive — just a UI hint for the banner) and reload to /app.
 *   4. <ImpersonationBanner /> reads the JWT payload via /api/auth/me which
 *      now contains `impersonator_id`, and shows the yellow banner.
 *   5. "Exit impersonation" → POST /api/owner/impersonate/stop, which
 *      restores the owner cookies; banner clears.
 */

import { useEffect, useState } from "react";
import { Warning, SignOut } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";

export const IMPERSONATION_HINT = "hbf_impersonating"; // UI-only sessionStorage hint

export async function startImpersonation(targetUserId, targetUserSummary) {
  await api.post(`/owner/users/${targetUserId}/impersonate`);
  try { sessionStorage.setItem(IMPERSONATION_HINT, JSON.stringify(targetUserSummary || {})); } catch (_) { /* noop */ }
  window.location.href = "/app";
}

export async function endImpersonation() {
  try { await api.post("/owner/impersonate/stop"); }
  catch (e) {
    console.warn("[impersonation] stop failed:", formatApiError(e.response?.data?.detail));
  }
  try { sessionStorage.removeItem(IMPERSONATION_HINT); } catch (_) { /* noop */ }
  window.location.href = "/owner";
}

export function getImpersonation() {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_HINT);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function ImpersonationBanner() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    setInfo(getImpersonation());
    // Best-effort: refresh from /auth/me so the banner is honest even if the
    // sessionStorage hint went missing across tabs.
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (cancel) return;
        if (data && data.impersonator_id) {
          setInfo((prev) => prev || { id: data.id, email: data.email, name: data.name, role: data.role, org_id: data.org_id, org_name: data.organization?.name });
        }
      } catch (_) { /* noop */ }
    })();
    return () => { cancel = true; };
  }, []);

  if (!info) return null;
  return (
    <div className="impersonation-banner" data-testid="impersonation-banner">
      <div className="flex items-center gap-2">
        <Warning size={14} weight="fill" />
        <span>Owner impersonation: viewing as {info.name || info.email} ({info.role} · {info.org_name || info.org_id})</span>
      </div>
      <button onClick={endImpersonation} data-testid="exit-impersonation">
        <SignOut size={11} className="inline mr-1" /> Exit impersonation
      </button>
    </div>
  );
}
